import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Building2, Loader2, Check, Crown, Zap,
  PartyPopper
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';

const API = API_BASE;

const WorkspaceCreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [purchase, setPurchase] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const purchaseId = searchParams.get('purchase_id');

  useEffect(() => {
    if (purchaseId) {
      fetchPurchaseDetails();
    } else {
      // No purchase_id - redirect to purchase page
      toast.error('No purchase found. Please purchase a workspace first.');
      navigate('/workspace/purchase');
    }
  }, [purchaseId, navigate]);

  const fetchPurchaseDetails = async () => {
    try {
      const response = await axios.get(`${API}/razorpay/workspace/purchase/${purchaseId}`, {
        headers: getAuthHeaders()
      });
      
      const purchaseData = response.data;
      
      // Check if purchase is valid
      if (purchaseData.payment_status !== 'paid') {
        toast.error('Payment not completed. Please complete payment first.');
        navigate('/workspace/purchase');
        return;
      }
      
      // Check if already used
      if (purchaseData.workspace_id) {
        toast.info('This purchase has already been used to create a workspace.');
        navigate('/');
        return;
      }
      
      setPurchase(purchaseData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching purchase:', error);
      toast.error('Invalid purchase. Please try again.');
      navigate('/workspace/purchase');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a workspace name');
      return;
    }

    setCreating(true);

    try {
      const response = await axios.post(`${API}/workspaces`, {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        purchase_id: purchaseId
      }, {
        headers: getAuthHeaders()
      });

      toast.success(`${purchase.workspace_type === 'studio' ? 'Studio' : 'Pro'} workspace created successfully!`);
      
      // Navigate to the new workspace
      navigate(`/workspaces/${response.data.id}`);
    } catch (error) {
      console.error('Error creating workspace:', error);
      toast.error(error.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading purchase details...</p>
        </div>
      </div>
    );
  }

  const Icon = purchase?.workspace_type === 'studio' ? Crown : Zap;
  const typeColor = purchase?.workspace_type === 'studio' ? 'text-amber-500' : 'text-blue-500';
  const typeBgColor = purchase?.workspace_type === 'studio' ? 'bg-amber-500/10' : 'bg-blue-500/10';

  return (
    <div className="bg-background">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12 max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="-ml-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">Create Workspace</h1>
          </div>
        </div>
        {/* Success Banner */}
        <Card className="mb-8 p-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <PartyPopper className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-green-800 dark:text-green-200">
                Payment Successful!
              </h3>
              <p className="text-sm text-green-700 dark:text-green-300">
                Your {purchase?.workspace_type === 'studio' ? 'Studio' : 'Pro'} workspace purchase is complete. 
                Now let's set up your workspace.
              </p>
            </div>
          </div>
        </Card>

        {/* Workspace Type Badge */}
        <div className="flex items-center justify-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${typeBgColor}`}>
            <Icon className={`h-5 w-5 ${typeColor}`} />
            <span className={`font-semibold ${typeColor}`}>
              {purchase?.workspace_type === 'studio' ? 'Studio' : 'Pro'} Workspace
            </span>
          </div>
        </div>

        {/* Create Form */}
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Workspace Name *</Label>
              <Input
                id="name"
                placeholder="e.g., My Production Studio"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={100}
                required
              />
              <p className="text-xs text-muted-foreground">
                Choose a name that represents your team or project
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="What will this workspace be used for?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={500}
                rows={3}
              />
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Check className="h-4 w-4 text-green-500" />
                <span>
                  {purchase?.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} subscription • 
                  Renews {purchase?.current_period_end ? new Date(purchase.current_period_end).toLocaleDateString('en-IN') : 'automatically'}
                </span>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={creating || !formData.name.trim()}
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating Workspace...
                  </>
                ) : (
                  <>
                    <Building2 className="h-4 w-4 mr-2" />
                    Create Workspace
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
};

export default WorkspaceCreatePage;
